import { getUsersByWorkEngagement } from '@/app/dashboard/technical-management/utils';
import type { FilterObject, JsonFilter } from '@/hooks/use-table-filter/types';
import { ObjectEntries } from '@/lib/utils';
import { getSessionValues } from '@/providers/session-provider/get-session-values';
import { DateTime } from 'luxon';

type RewriteResult = {
  key: string;
  value: FilterObject;
};

/**
 * Filter rewrites are definitions of filters that are only supported in frontend
 * and need to be converted to another filter that is supported in backend.
 * For example, `accepted_by_work_engagement` will fetch users in the work engagement
 * and change the filter to `users_accepted_delegations`.
 */
type FilterRewriteRule = {
  key: string;
  rewrite: (
    filter: FilterObject
  ) => Promise<RewriteResult | null> | RewriteResult | null;
};

export const CURRENT_USER_FILTER_SENTINEL = '__current_user__';
export const LAST_2_WEEKS_FILTER_SENTINEL = '__last_2_weeks__';

/**
 * Resolve CURRENT_USER_FILTER_SENTINEL to actual userId in an id array.
 * Use in UI (RelationDisplay, RelationModal) so we never send the sentinel to APIs
 * and so the current user appears selected. If no userId, sentinel is filtered out.
 */
export function resolveCurrentUserInFilterIds(ids: string[]): string[] {
  if (ids.length === 0) {
    return [];
  }
  const { userId } = getSessionValues();
  if (!userId) {
    return ids.filter((id) => id !== CURRENT_USER_FILTER_SENTINEL);
  }
  const replaced = ids.map((id) =>
    id === CURRENT_USER_FILTER_SENTINEL ? userId : id
  );
  return [...new Set(replaced)];
}

/**
 * If value contains the sentinel, replace it with the appropriate value and return the new value.
 */
function replaceSentinel(
  value: string | number | string[] | boolean | null | undefined
): string | string[] | undefined {
  const { userId } = getSessionValues();

  if (value === undefined || value === null) {
    return undefined;
  }

  if (value === LAST_2_WEEKS_FILTER_SENTINEL) {
    return DateTime.now().minus({ weeks: 2 }).toISODate();
  }

  if (value === CURRENT_USER_FILTER_SENTINEL) {
    return userId;
  }

  if (Array.isArray(value)) {
    if (!value.includes(CURRENT_USER_FILTER_SENTINEL)) {
      return undefined;
    }
    const replaced = value.map((v) =>
      v === CURRENT_USER_FILTER_SENTINEL ? userId : v
    );
    return [...new Set(replaced)];
  }
  return undefined;
}

function createSentinelRewriteRule(key: string): FilterRewriteRule {
  return {
    key,
    rewrite: (filter: FilterObject) => {
      let hasRewrite = false;
      const next: FilterObject = { ...filter };

      for (const [operator, value] of Object.entries(filter)) {
        const rewritten = replaceSentinel(value);
        if (rewritten !== undefined) {
          next[operator as keyof FilterObject] = rewritten;
          hasRewrite = true;
        }
      }

      if (!hasRewrite) {
        return { key, value: filter };
      }
      return { key, value: next };
    },
  };
}

export function rewriteDateWindowFilter(
  key: string,
  filter: FilterObject
): RewriteResult | null {
  const today = DateTime.now();

  const parseDays = (v: unknown): number | null => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    const rounded = Math.round(n);
    return rounded >= 1 ? rounded : null;
  };

  let start: DateTime | undefined;
  let end: DateTime | undefined;
  let before: DateTime | undefined;
  let after: DateTime | undefined;

  if (
    filter.__date_past == null &&
    filter.__date_future == null &&
    filter.__date_time_past == null &&
    filter.__date_time_future == null &&
    filter.__date_past_beyond == null &&
    filter.__date_future_beyond == null &&
    filter.__date_time_past_beyond == null &&
    filter.__date_time_future_beyond == null
  ) {
    return null;
  }

  const useDateOnlyFormat =
    filter.__date_past != null ||
    filter.__date_future != null ||
    filter.__date_past_beyond != null ||
    filter.__date_future_beyond != null;

  const formatValue = (dt: DateTime): string =>
    useDateOnlyFormat ? (dt.toISODate() ?? '') : (dt.toUTC().toISO() ?? '');

  if (filter.__date_time_past != null || filter.__date_past != null) {
    const days = parseDays(filter.__date_time_past ?? filter.__date_past);

    if (days) {
      start = today.minus({ days: days - 1 }).startOf('day');
      end = today.endOf('day');
    }
  }

  if (filter.__date_time_future != null || filter.__date_future != null) {
    const days = parseDays(filter.__date_time_future ?? filter.__date_future);

    if (days) {
      const futureStart = today.plus({ days: 1 }).startOf('day');
      const futureEnd = today.plus({ days }).endOf('day');

      if (start) {
        end = futureEnd;
      } else {
        start = futureStart;
        end = futureEnd;
      }
    }
  }

  // Open-ended complements: "mer än X dagar sedan" is everything before the start
  // of "Senaste X", "mer än X dagar framåt" everything after the end of "Kommande X".
  if (
    filter.__date_time_past_beyond != null ||
    filter.__date_past_beyond != null
  ) {
    const days = parseDays(
      filter.__date_time_past_beyond ?? filter.__date_past_beyond
    );

    if (days) {
      before = today.minus({ days: days - 1 }).startOf('day');
    }
  }

  if (
    filter.__date_time_future_beyond != null ||
    filter.__date_future_beyond != null
  ) {
    const days = parseDays(
      filter.__date_time_future_beyond ?? filter.__date_future_beyond
    );

    if (days) {
      after = today.plus({ days }).endOf('day');
    }
  }

  return {
    key,
    value: {
      ...(start ? { __gte: formatValue(start) } : {}),
      ...(end ? { __lte: formatValue(end) } : {}),
      ...(before ? { __lt: formatValue(before) } : {}),
      ...(after ? { __gt: formatValue(after) } : {}),
    },
  };
}

const filterRewrites = [
  {
    key: 'tags_exclude', // old filter name, rewritten to use operator but we dont want to break bookmarks
    rewrite: (filter: FilterObject) => {
      const tagIds = filter.noop;

      return {
        key: 'tags!',
        value: {
          noop: tagIds,
        },
      };
    },
  },
  {
    key: 'unanswered_by_work_engagement',
    rewrite: async (filter: FilterObject) => {
      const workEngagementId = Array.isArray(filter.noop)
        ? filter.noop.at(0)
        : null;
      if (!workEngagementId) {
        return null;
      }

      const userIds = await getUsersByWorkEngagement(workEngagementId, {
        queryParams: { pageSize: 30 },
      });

      return {
        key: 'users_unanswered_delegations',
        value: {
          noop: userIds,
        },
      };
    },
  },
  {
    key: 'accepted_by_work_engagement',
    rewrite: async (filter: FilterObject) => {
      const workEngagementId = Array.isArray(filter.noop)
        ? filter.noop.at(0)
        : null;
      if (!workEngagementId) {
        return null;
      }

      // Fetch a maximum of 30 because more could make the url too long.
      const userIds = await getUsersByWorkEngagement(workEngagementId, {
        queryParams: { pageSize: 30 },
      });

      return {
        key: 'users_accepted_delegations',
        value: {
          noop: userIds,
        },
      };
    },
  },
] as const satisfies FilterRewriteRule[];

export async function rewriteFilter(
  jsonFilter: JsonFilter | undefined
): Promise<JsonFilter | undefined> {
  if (!jsonFilter) {
    return undefined;
  }

  const filterEntries = ObjectEntries(jsonFilter);

  const rewritePromises = filterEntries.map(async ([key, value]) => {
    if (!value) {
      return null;
    }

    // Date window filter rewrite is applied to all date window filters and not key specific filters
    const dateWindowRewrite = rewriteDateWindowFilter(key, value);
    if (dateWindowRewrite) {
      return [dateWindowRewrite.key, dateWindowRewrite.value] as const;
    }

    const rewriteRule = filterRewrites.find((rewrite) => rewrite.key === key);
    if (rewriteRule) {
      try {
        const rewritten = await rewriteRule.rewrite(value);
        if (!rewritten) {
          return null;
        }
        return [rewritten.key, rewritten.value] as const;
      } catch {
        console.error(`Failed to rewrite filter with key "${key}"`);
        return null;
      }
    }

    const sentinelRewrite = createSentinelRewriteRule(key);
    if (sentinelRewrite) {
      try {
        const rewritten = await sentinelRewrite.rewrite(value);
        if (!rewritten) {
          return null;
        }
        return [rewritten.key, rewritten.value] as const;
      } catch {
        console.error(`Failed to rewrite filter with key "${key}"`);
        return null;
      }
    }
    return [key, value] as const;
  });

  const rewrittenEntries = await Promise.all(rewritePromises);

  return Object.fromEntries(rewrittenEntries.filter(Boolean)) as JsonFilter;
}
